import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HUB_PROPERTIES, HUB_SEO_DEFAULTS } from '../pages/guest-hub/hubConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default SEO values
const defaultSEO = {
  home: {
    meta_title: 'Capitol View Stays | Premium DC Vacation Rentals',
    meta_description: 'Book premium vacation rentals in Washington DC. Fully furnished homes near Capitol Hill, monuments & museums. Best rates when you book direct!',
    meta_keywords: 'Washington DC vacation rental, Capitol Hill Airbnb, DC short term rental, furnished rental DC'
  },
  properties: {
    meta_title: 'Our Properties | Capitol View Stays',
    meta_description: 'Browse our collection of premium vacation rentals in Washington DC. From cozy condos to spacious townhouses near Capitol Hill.',
    meta_keywords: 'DC rental properties, Capitol Hill rentals, Washington DC homes'
  },
  about: {
    meta_title: 'About Us | Capitol View Stays',
    meta_description: 'Learn about Capitol View Stays - your trusted source for premium short-term rentals in Washington DC since 2018.',
    meta_keywords: 'Capitol View Stays, DC property management, vacation rental company'
  },
  blog: {
    meta_title: 'Blog | Capitol View Stays',
    meta_description: 'Travel tips, DC guides, and insider knowledge for your Washington DC visit from Capitol View Stays.',
    meta_keywords: 'DC travel blog, Washington DC tips, Capitol Hill guide'
  },
  contact: {
    meta_title: 'Contact Us | Capitol View Stays',
    meta_description: 'Get in touch with Capitol View Stays. We\'re here to help with your Washington DC vacation rental needs.',
    meta_keywords: 'contact Capitol View Stays, DC rental inquiry'
  },
  'house-rules': {
    meta_title: 'House Rules | Capitol View Stays',
    meta_description: 'Review our house rules and policies for a comfortable stay at Capitol View Stays properties.',
    meta_keywords: 'rental policies, house rules, guest guidelines'
  },
  ...HUB_SEO_DEFAULTS
};

const HUB_PAGE_RE = /^\/([^/]+)\/(wifi|guidebook|local-guide|social|book)\/?$/;
const PROPERTY_RE = /^\/property\/([^/]+)\/?$/;

const hubPropertyName = (path) => {
  const slug = path.startsWith('/hub/') ? path.split('/')[2] : (path.match(HUB_PAGE_RE) || path.match(PROPERTY_RE))?.[1];
  return HUB_PROPERTIES.find((p) => p.slug === slug || p.id === slug)?.name || '';
};

const propertyDefaults = {
  meta_title: '{property} | Washington DC Vacation Rental | Capitol View Stays',
  meta_description: 'Book {property} directly with Capitol View Stays for the best rates. Fully furnished Washington DC rental with fast Wi-Fi and self check-in.',
  meta_keywords: 'Washington DC vacation rental, Capitol Hill rental, book direct DC',
};

const SEOHead = () => {
  const location = useLocation();
  const [seoData, setSeoData] = useState(null);

  // Determine current page from pathname
  const getPageKey = () => {
    const path = location.pathname;
    if (path === '/' || path === '') return 'home';
    if (path === '/hub' || path.startsWith('/hub/')) return 'hub';
    const hubMatch = path.match(HUB_PAGE_RE);
    if (hubMatch && HUB_PROPERTIES.some((p) => p.slug === hubMatch[1] || p.id === hubMatch[1])) return `hub-${hubMatch[2]}`;
    const propMatch = path.match(PROPERTY_RE);
    if (propMatch) return `property-${propMatch[1]}`;
    if (path.startsWith('/properties') || path.startsWith('/property')) return 'properties';
    if (path.startsWith('/about')) return 'about';
    if (path.startsWith('/blog')) return 'blog';
    if (path.startsWith('/contact')) return 'contact';
    if (path.startsWith('/house-rules')) return 'house-rules';
    if (path.startsWith('/local-guide')) return 'properties';
    return 'home';
  };

  const pageKey = getPageKey();
  const hubGuidebookProp = pageKey === 'hub-guidebook' ? HUB_PROPERTIES.find((p) => p.slug === location.pathname.split('/')[1] || p.id === location.pathname.split('/')[1]) : null;

  useEffect(() => {
    setSeoData(null);
    const fetchSEO = async () => {
      try {
        if (hubGuidebookProp) {
          const own = await fetch(`${API_URL}/api/public/seo/guidebook-${hubGuidebookProp.id}`);
          if (own.ok) {
            const data = await own.json();
            if (data.meta_title || data.meta_description) return setSeoData(data);
          }
        }
        const response = await fetch(`${API_URL}/api/public/seo/${pageKey}`);
        if (response.ok) {
          const data = await response.json();
          setSeoData(data.meta_title || data.meta_description || data.no_index ? data : null);
        }
      } catch (error) {
        console.log('Using default SEO settings');
      }
    };

    fetchSEO();
  }, [pageKey, hubGuidebookProp?.id]);

  // Use fetched data or fall back to defaults
  const defaults = defaultSEO[pageKey] || (pageKey.startsWith('property-') ? propertyDefaults : defaultSEO.home);
  const raw = seoData?.meta_title 
    ? seoData 
    : { ...defaults, no_index: seoData?.no_index ?? defaults.no_index };
  const propertyName = hubPropertyName(location.pathname) || 'Capitol View Stays';
  const fill = (s) => (s || '').replace(/\{property\}/gi, propertyName);
  const seo = { ...raw, meta_title: fill(raw.meta_title), meta_description: fill(raw.meta_description) };
  const robots = raw.no_index ? 'noindex, nofollow' : 'index, follow';

  const siteUrl = 'https://capitol-view-stay.com';
  const currentUrl = `${siteUrl}${location.pathname}`;

  useEffect(() => {
    document.title = seo.meta_title;
    const tags = [
      ['name', 'title', seo.meta_title],
      ['name', 'description', seo.meta_description],
      ['name', 'keywords', seo.meta_keywords],
      ['name', 'robots', robots],
      ['name', 'author', 'Capitol View Stays'],
      ['name', 'geo.region', 'US-DC'],
      ['name', 'geo.placename', 'Washington, DC'],
      ['name', 'twitter:card', 'summary_large_image'],
      ['name', 'twitter:url', currentUrl],
      ['name', 'twitter:title', seo.meta_title],
      ['name', 'twitter:description', seo.meta_description],
      ['name', 'twitter:image', seo.og_image],
      ['property', 'og:type', 'website'],
      ['property', 'og:url', currentUrl],
      ['property', 'og:title', seo.meta_title],
      ['property', 'og:description', seo.meta_description],
      ['property', 'og:image', seo.og_image],
      ['property', 'og:site_name', 'Capitol View Stays'],
    ];
    tags.forEach(([attr, key, value]) => upsertMeta(attr, key, value));
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);
  }, [seo.meta_title, seo.meta_description, seo.meta_keywords, seo.og_image, robots, currentUrl]);

  return null;
};

const upsertMeta = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!value) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

export default SEOHead;
